import React, { useState, useRef, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import adminLogo from "../../../../Assets/Images/Logo.svg";
import collapsibleAdminLogo from "../../../../Assets/Images/CollapsibleLogo.svg";
import {
  FIXED_SIDEBAR_WIDTH_OPEN,
  FIXED_SIDEBAR_WIDTH_CLOSED,
  FIXED_HEADER_HEIGHT,
} from "../../../../utils/constant.utils";
import { AppContext } from "../../../../context/appContext";

export default function Sidebar({ isSidebarOpen }) {
  const { t, isRTL } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const toggleRef = useRef(null); // Reference for the div that toggles the menu

  const [cmsMenuOpen, setCmsMenuOpen] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith("/admin/cms")) {
      setCmsMenuOpen(true);
    } else {
      setCmsMenuOpen(false);
    }
  }, [location.pathname, isSidebarOpen]);

  const isActive = (path) => location.pathname === path;

  const handleSubMenuClick = (path) => {
    // Navigate to the clicked path
    navigate(path);

    // Ensure the CMS menu stays open
    if (!cmsMenuOpen) {
      setCmsMenuOpen(true); // Keeps CMS menu open when a submenu item is clicked
    }
  };

  // Close the menu when clicking outside
  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close menu if the click is outside both the menu and toggle div
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        setCmsMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);
  return (
    <div
      style={{
        minWidth: isSidebarOpen
          ? `${FIXED_SIDEBAR_WIDTH_OPEN}px`
          : `${FIXED_SIDEBAR_WIDTH_CLOSED}px`,
        maxWidth: isSidebarOpen
          ? `${FIXED_SIDEBAR_WIDTH_OPEN}px`
          : `${FIXED_SIDEBAR_WIDTH_CLOSED}px`,
        width: isSidebarOpen
          ? `${FIXED_SIDEBAR_WIDTH_OPEN}px`
          : `${FIXED_SIDEBAR_WIDTH_CLOSED}px`,
        [isRTL ? 'right' : 'left']: 0,
      }}
      className="min-h-screen bg-[#090A13] text-white shadow-md pb-16 transition-all ease-in-out duration-300"
    >
      {/* Logo */}
      <div
        style={{
          minHeight: `${FIXED_HEADER_HEIGHT}px`,
          maxHeight: `${FIXED_HEADER_HEIGHT}px`,
        }}
        className={
          isSidebarOpen
            ? `${isRTL ? 'pr-[35px]' : 'pl-[35px]'} flex justify-start items-center`
            : `px-[25px] flex justify-start items-center`
        }
      >
        {typeof isSidebarOpen === "boolean" ? (
          <img
            src={isSidebarOpen ? adminLogo : collapsibleAdminLogo}
            alt="Admin Logo"
            className="w-auto transition-opacity duration-200"
          />
        ) : (
          <div className="w-[120px] h-[40px] bg-gray-800 animate-pulse rounded"></div>
        )}
      </div>

      <nav
        className={`h-[calc(100vh-80px)] overflow-y-auto scrollbar-hide ${
          isSidebarOpen ? "p-[25px]" : "p-4"
        } space-y-2 border-t border-[#393941]`}
      >
        {/* Dashboard */}
        <MenuItem
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 16 16"
              fill="none"
            >
              <g clip-path="url(#clip0_335_4964)">
                <path
                  d="M1.3335 8.13599C1.3335 6.61037 1.3335 5.84756 1.67963 5.2152C2.02576 4.58285 2.65813 4.19038 3.92285 3.40546L5.25618 2.57795C6.59309 1.74823 7.26154 1.33337 8.00016 1.33337C8.73879 1.33337 9.40724 1.74823 10.7441 2.57795L12.0775 3.40546C13.3422 4.19038 13.9746 4.58285 14.3207 5.2152C14.6668 5.84756 14.6668 6.61037 14.6668 8.13599V9.15001C14.6668 11.7506 14.6668 13.0509 13.8858 13.8588C13.1047 14.6667 11.8477 14.6667 9.3335 14.6667H6.66683C4.15267 14.6667 2.89559 14.6667 2.11454 13.8588C1.3335 13.0509 1.3335 11.7506 1.3335 9.15001V8.13599Z"
                  stroke="currentColor"
                />
                <path
                  d="M10 12H6"
                  stroke="currentColor"
                  stroke-linecap="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_335_4964">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>
          }
          label={t('nav.dashboard')}
          path="/admin/dashboard"
          active={isActive("/admin/dashboard")}
          isSidebarOpen={isSidebarOpen}
          navigate={navigate}
        />

        {/* Books */}
        <MenuItem
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M13.3881 2.01187L13.4056 2.51157L13.3881 2.01187ZM11.0002 2.325L10.8568 1.84601V1.84601L11.0002 2.325ZM9.11606 3.20188L8.8656 2.76912L8.8656 2.76912L9.11606 3.20188ZM2.6551 2.04997L2.62443 2.54902L2.6551 2.04997ZM4.66683 2.325L4.7944 1.84155H4.7944L4.66683 2.325ZM6.855 3.25035L6.62127 3.69236H6.62127L6.855 3.25035ZM9.08522 13.3795L9.32042 13.8208H9.32042L9.08522 13.3795ZM11.3335 12.4223L11.2059 11.9388L11.3335 12.4223ZM13.3236 12.1486L13.3548 12.6476H13.3548L13.3236 12.1486ZM6.91511 13.3795L6.67991 13.8208L6.91511 13.3795ZM4.66683 12.4223L4.7944 11.9388H4.7944L4.66683 12.4223ZM2.67674 12.1486L2.64552 12.6476H2.64552L2.67674 12.1486ZM1.8335 10.7624V3.33191H0.833496V10.7624H1.8335ZM15.1668 10.7624V3.28884H14.1668V10.7624H15.1668ZM13.3707 1.51218C12.6119 1.53861 11.6199 1.61755 10.8568 1.84601L11.1436 2.804C11.7763 2.61456 12.6597 2.53755 13.4056 2.51157L13.3707 1.51218ZM10.8568 1.84601C10.1934 2.0446 9.44726 2.43249 8.8656 2.76912L9.36651 3.63463C9.93348 3.3065 10.5967 2.9677 11.1436 2.804L10.8568 1.84601ZM2.62443 2.54902C3.26842 2.5886 3.99778 2.66557 4.53926 2.80845L4.7944 1.84155C4.15386 1.67253 3.34361 1.59134 2.68577 1.55091L2.62443 2.54902ZM4.53926 2.80845C5.18067 2.97771 5.97007 3.34801 6.62127 3.69236L7.08872 2.80834C6.42507 2.45741 5.55194 2.04145 4.7944 1.84155L4.53926 2.80845ZM9.32042 13.8208C9.9811 13.4686 10.7994 13.0803 11.4611 12.9057L11.2059 11.9388C10.4257 12.1447 9.52199 12.5801 8.85002 12.9383L9.32042 13.8208ZM11.4611 12.9057C11.9964 12.7645 12.7156 12.6876 13.3548 12.6476L13.2924 11.6496C12.639 11.6904 11.8395 11.7716 11.2059 11.9388L11.4611 12.9057ZM7.15031 12.9383C6.47834 12.5801 5.57463 12.1447 4.7944 11.9388L4.53926 12.9057C5.20096 13.0803 6.01923 13.4686 6.67991 13.8208L7.15031 12.9383ZM4.7944 11.9388C4.16078 11.7716 3.36134 11.6904 2.70795 11.6496L2.64552 12.6476C3.2847 12.6876 4.00394 12.7645 4.53926 12.9057L4.7944 11.9388ZM14.1668 10.7624C14.1668 11.2196 13.788 11.6186 13.2924 11.6496L13.3548 12.6476C14.3376 12.5861 15.1668 11.7866 15.1668 10.7624H14.1668ZM15.1668 3.28884C15.1668 2.3133 14.39 1.47667 13.3707 1.51218L13.4056 2.51157C13.8154 2.49729 14.1668 2.83444 14.1668 3.28884H15.1668ZM0.833496 10.7624C0.833496 11.7866 1.66269 12.5861 2.64552 12.6476L2.70795 11.6496C2.21237 11.6186 1.8335 11.2196 1.8335 10.7624H0.833496ZM8.85002 12.9383C8.32183 13.2199 7.67849 13.2199 7.15031 12.9383L6.67991 13.8208C7.50211 14.259 8.49821 14.259 9.32042 13.8208L8.85002 12.9383ZM8.8656 2.76912C8.32021 3.08477 7.64127 3.10051 7.08872 2.80834L6.62127 3.69236C7.48276 4.14789 8.52749 4.12021 9.36651 3.63463L8.8656 2.76912ZM1.8335 3.33191C1.8335 2.86712 2.20178 2.52305 2.62443 2.54902L2.68577 1.55091C1.64694 1.48706 0.833496 2.33247 0.833496 3.33191H1.8335Z"
                fill="currentColor"
              />
              <path d="M8 3.90271V14" stroke="currentColor" />
              <path
                d="M3.3335 6L6.00016 6.66667"
                stroke="currentColor"
                stroke-linecap="round"
              />
              <path
                d="M12.6665 6L9.99984 6.66667"
                stroke="currentColor"
                stroke-linecap="round"
              />
              <path
                d="M3.3335 8.66663L6.00016 9.33329"
                stroke="currentColor"
                stroke-linecap="round"
              />
              <path
                d="M12.6665 8.66663L9.99984 9.33329"
                stroke="currentColor"
                stroke-linecap="round"
              />
            </svg>
          }
          label={t('nav.books')}
          path="/admin/books"
          active={isActive("/admin/books")}
          isSidebarOpen={isSidebarOpen}
          navigate={navigate}
        />

        {/* Videos */}
        <MenuItem
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M2 5.33333C2 3.77563 2 2.99678 2.43934 2.4799C2.87868 1.96301 3.60649 1.89074 5.06211 1.74621L6.39544 1.62554C9.0564 1.37069 10.3869 1.24326 11.3601 1.93876C12.3333 2.63426 12.6667 3.92019 12.6667 6.49206V9.50794C12.6667 12.0798 12.6667 13.3657 11.3601 14.0612C10.3869 14.7567 9.0564 14.6293 6.39544 14.3745L5.06211 14.2538C3.60649 14.1093 2.87868 14.037 2.43934 13.5201C2 13.0032 2 12.2244 2 10.6667V5.33333Z"
                stroke="currentColor"
              />
              <path
                d="M12.6667 6L13.7019 6.46924C14.0599 6.62654 14.2389 6.70519 14.2695 6.86184C14.3 7.01849 14.1644 7.16739 13.8931 7.46518L12.6667 8.8"
                stroke="currentColor"
                stroke-linecap="round"
              />
            </svg>
          }
          label={t('nav.videos')}
          path="/admin/videos"
          active={isActive("/admin/videos")}
          isSidebarOpen={isSidebarOpen}
          navigate={navigate}
        />

        {/* Series (The Chosen / برگزیدگان) */}
        <MenuItem
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="none">
              <rect x="1.8" y="3.2" width="12.4" height="9.6" rx="2" stroke="currentColor" />
              <path d="M6.5 6.2L10 8L6.5 9.8V6.2Z" fill="currentColor" />
              <path d="M5 1.7L6.2 3.2" stroke="currentColor" strokeLinecap="round" />
              <path d="M9.5 1.7L8.3 3.2" stroke="currentColor" strokeLinecap="round" />
            </svg>
          }
          label={t('nav.series')}
          path="/admin/series"
          active={isActive("/admin/series")}
          isSidebarOpen={isSidebarOpen}
          navigate={navigate}
        />

        {/* Audio */}
        <MenuItem
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M2 6.66667C2 5.19391 2 4.45753 2.39544 4.02236C2.79088 3.58719 3.46667 3.58719 4.81825 3.58719H5.66667C6.27536 3.58719 6.5797 3.58719 6.86824 3.66231C7.15677 3.73744 7.41594 3.88363 7.93428 4.176L8.06572 4.25733C8.58406 4.5497 8.84323 4.69589 9.13176 4.77102C9.4203 4.84614 9.72464 4.84614 10.3333 4.84614H11.1818C12.5333 4.84614 13.2091 4.84614 13.6046 5.28131C14 5.71648 14 6.45286 14 7.92562V9.18457C14 10.6573 14 11.3937 13.6046 11.8289C13.2091 12.2641 12.5333 12.2641 11.1818 12.2641H4.81825C3.46667 12.2641 2.79088 12.2641 2.39544 11.8289C2 11.3937 2 10.6573 2 9.18457V6.66667Z"
                stroke="currentColor"
              />
              <path
                d="M10.6667 7.92578V10.5539"
                stroke="currentColor"
                stroke-linecap="round"
              />
              <path
                d="M8.66667 8.55078V9.92883"
                stroke="currentColor"
                stroke-linecap="round"
              />
              <path
                d="M6.66667 7.30078V11.1788"
                stroke="currentColor"
                stroke-linecap="round"
              />
              <path
                d="M4.66667 8.55078V9.92883"
                stroke="currentColor"
                stroke-linecap="round"
              />
            </svg>
          }
          label={t('nav.audio')}
          path="/admin/cms/audio"
          active={isActive("/admin/cms/audio")}
          isSidebarOpen={isSidebarOpen}
          navigate={navigate}
        />

        {/* Bible */}
        <MenuItem
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M2.6665 5.33325C2.6665 3.44763 2.6665 2.50482 3.25229 1.91904C3.83808 1.33325 4.78089 1.33325 6.6665 1.33325H9.33317C11.2188 1.33325 12.1616 1.33325 12.7474 1.91904C13.3332 2.50482 13.3332 3.44763 13.3332 5.33325V10.6666C13.3332 12.5522 13.3332 13.495 12.7474 14.0808C12.1616 14.6666 11.2188 14.6666 9.33317 14.6666H6.6665C4.78089 14.6666 3.83808 14.6666 3.25229 14.0808C2.6665 13.495 2.6665 12.5522 2.6665 10.6666V5.33325Z"
                stroke="currentColor"
              />
              <path
                d="M13.265 10.6667H5.26502C4.64504 10.6667 4.33505 10.6667 4.08072 10.7349C3.39053 10.9198 2.85144 11.4589 2.6665 12.1491"
                stroke="currentColor"
              />
              <path
                d="M5.3335 4.66675H10.6668"
                stroke="currentColor"
                stroke-linecap="round"
              />
              <path
                d="M5.3335 7H8.66683"
                stroke="currentColor"
                stroke-linecap="round"
              />
              <path
                d="M8.66667 10.6667V13.0207C8.66667 13.2044 8.66667 13.2963 8.60343 13.3334C8.5402 13.3705 8.45371 13.3295 8.28074 13.2473L7.45259 12.8539C7.39407 12.8261 7.36481 12.8122 7.33333 12.8122C7.30186 12.8122 7.2726 12.8261 7.21408 12.8539L6.38592 13.2473C6.21296 13.3295 6.12647 13.3705 6.06324 13.3334C6 13.2963 6 13.2044 6 13.0207V10.9668"
                stroke="currentColor"
                stroke-linecap="round"
              />
            </svg>
          }
          label={t('nav.bible')}
          path="/admin/bible"
          active={isActive("/admin/bible")}
          isSidebarOpen={isSidebarOpen}
          navigate={navigate}
        />

        {/* CMS with Submenu */}
        <div>
          <div
            className={`flex items-center justify-between cursor-pointer p-3 px-4 rounded-md transition-colors ${
              cmsMenuOpen
                ? "bg-[var(--primary-color)] text-[#0D6EFD]"
                : "hover:bg-gray-100 hover:text-black"
            }`}
            onClick={() => setCmsMenuOpen((prev) => !prev)}
            ref={toggleRef}
          >
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="none"
              >
                <g clip-path="url(#clip0_335_14743)">
                  <path
                    d="M8.00016 1.3335C11.6821 1.3335 14.6668 4.31826 14.6668 8.00016C14.6668 11.6821 11.6821 14.6668 8.00016 14.6668C4.31826 14.6668 1.3335 11.6821 1.3335 8.00016C1.3335 6.79447 1.65356 5.66353 2.21337 4.68768"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M3.3335 8.00016C3.3335 10.5775 5.42283 12.6668 8.00016 12.6668C10.5775 12.6668 12.6668 10.5775 12.6668 8.00016C12.6668 5.42283 10.5775 3.3335 8.00016 3.3335"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 10.6668C9.47276 10.6668 10.6667 9.47292 10.6667 8.00016C10.6667 6.5274 9.47276 5.3335 8 5.3335"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_335_14743">
                    <rect width="16" height="16" fill="currentColor" />
                  </clipPath>
                </defs>
              </svg>

              {isSidebarOpen && (
                <span className="text-sm pl-1 font-semibold">{t('nav.cms')}</span>
              )}
            </div>
            {cmsMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            )}
          </div>

          {cmsMenuOpen && isSidebarOpen && (
            <div className="space-y-3 pl-6 mt-5">
              <SubMenuItem
                label={t('nav.home')}
                path="/admin/cms/advertisement"
                active={isActive("/admin/cms/advertisement")}
                isSidebarOpen={isSidebarOpen}
                navigate={navigate}
              />
              <SubMenuItem
                label={t('nav.categories')}
                path="/admin/cms/categories"
                active={isActive("/admin/cms/categories")}
                isSidebarOpen={isSidebarOpen}
                navigate={navigate}
              />
              <SubMenuItem
                label={t('nav.aboutUs')}
                path="/admin/cms/about-us"
                active={isActive("/admin/cms/about-us")}
                isSidebarOpen={isSidebarOpen}
                navigate={navigate}
              />
              <SubMenuItem
                label={t('nav.sections')}
                path="/admin/cms/homeSection"
                active={isActive("/admin/cms/homeSection")}
                isSidebarOpen={isSidebarOpen}
                navigate={navigate}
              />
              <SubMenuItem
                label={t('nav.notifications')}
                path="/admin/cms/notifications"
                active={isActive("/admin/cms/notifications")}
                isSidebarOpen={isSidebarOpen}
                navigate={navigate}
              />
            </div>
          )}

          {!isSidebarOpen && cmsMenuOpen && (
            <ul
              ref={menuRef}
              className={`absolute menu bg-white text-black shadow-md rounded-box w-56 border ${isRTL ? 'right-[89px]' : 'left-[89px]'} mt-[-20px] z-50`}
            >
              <li>
                <a
                  href="/admin/cms/advertisement"
                  className={
                    isActive("/admin/cms/advertisement") ? "bg-[#0D6EFD] text-white" : ""
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/admin/cms/advertisement");
                  }}
                >
                  {t('nav.home')}
                </a>
              </li>
              <li>
                <a
                  href="/admin/cms/categories"
                  className={
                    isActive("/admin/cms/categories")
                      ? "bg-[#0D6EFD] text-white"
                      : ""
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/admin/cms/categories");
                  }}
                >
                  {t('nav.categories')}
                </a>
              </li>
              <li>
                <a
                  href="/admin/cms/about-us"
                  className={
                    isActive("/admin/cms/about-us")
                      ? "bg-[#0D6EFD] text-white"
                      : ""
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/admin/cms/about-us");
                  }}
                >
                  {t('nav.aboutUs')}
                </a>
              </li>
              <li>
                <a
                  href="/admin/cms/homeSection"
                  className={
                    isActive("/admin/cms/homeSection")
                      ? "bg-[#0D6EFD] text-white"
                      : ""
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/admin/cms/homeSection");
                  }}
                >
                  {t('nav.sections')}
                </a>
              </li>
              <li>
                <a
                  href="/admin/cms/notifications"
                  className={
                    isActive("/admin/cms/notifications")
                      ? "bg-[#0D6EFD] text-white"
                      : ""
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/admin/cms/notifications");
                  }}
                >
                  {t('nav.notifications')}
                </a>
              </li>
            </ul>
          )}
        </div>
      </nav>
    </div>
  );
}

// Reusable Menu Item
function MenuItem({ icon, label, path, active, isSidebarOpen, navigate }) {
  const tooltipRef = useRef(null);
  const [tooltipTop, setTooltipTop] = useState("0px");

  const handleMouseEnter = () => {
    const rect = tooltipRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltipTop(`${rect.top + rect.height / 2}px`);
    }
  };

  return (
    <div
      ref={tooltipRef}
      className={`relative group flex items-center gap-3 p-4 px-3.5 rounded-md cursor-pointer transition-colors ${
        active
          ? "bg-[#2e2f37] rounded-xl border-l-2 border-[#0D6EFD] text-white"
          : "hover:bg-gray-100 hover:rounded-xl hover:text-black text-white border-l-2 border-[#0a0b15]"
      }`}
      onClick={() => navigate(path)}
      onMouseEnter={() => !isSidebarOpen && handleMouseEnter()}
    >
      <span className="relative">
        {icon}

        {/* Tooltip */}
        {!isSidebarOpen && (
          <span
            style={{
              position: "fixed",
              left: `${FIXED_SIDEBAR_WIDTH_CLOSED}px`,
              top: tooltipTop,
            }}
            className="-translate-y-1/2 whitespace-nowrap bg-black text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50"
          >
            {label}
          </span>
        )}
      </span>

      {isSidebarOpen && <span className="text-sm font-semibold">{label}</span>}
    </div>
  );
}

// Reusable Submenu Item
function SubMenuItem({ label, path, active, isSidebarOpen, navigate }) {
  return (
    <div
      className={`cursor-pointer pt-2 transition-colors pb-2 px-4 border-l-2 hover:rounded-xl ${
        active
          ? " font-semibold border-[#4169e1] bg-[#2e2f37] text-white rounded-xl hover:rounded-xl"
          : "text-white hover:text-black hover:bg-gray-100 hover:rounded-xl border-transparent"
      }`}
      onClick={() => navigate(path)}
    >
      <span className="text-sm">{label}</span>
    </div>
  );
}
